import { HttpEventType, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ContractFieldChangeResponse } from '@interfaces/contracts/responses/contract-field-change.response';
import {
  CreateClientContractRequest,
  SaveClientContractTemplateRequest,
  UpdateClientContractRequest,
} from '@interfaces/companys/contracts/requests/client-contract.request';
import {
  ClientContractRevisionListItemResponse,
  ClientContractRevisionResponse,
} from '@interfaces/companys/contracts/responses/client-contract-revision.response';
import {
  ClientContractTemplateResponse,
  ClientContractTemplateVersionResponse,
} from '@interfaces/companys/contracts/responses/client-contract-template.response';
import {
  ClientContractAutomaticPreviewResponse,
  ClientContractFieldCatalogResponse,
  ClientContractGroupCompany,
  ClientContractListItemResponse,
  ClientContractResponse,
  ClientContractType,
  ClientContractVariableResponse,
} from '@interfaces/companys/contracts/responses/client-contract.response';
import { VaultApiService } from '@services/api/vault-api.service';
import {
  ContractPdfFile,
  unwrapContractResult,
} from '@services/http/contracts/unwrap-contract-result.operator';
import { fileNameFromContentDisposition } from '@src/app/core/utils/zip-download.utils';
import { Observable, filter, map } from 'rxjs';

/**
 * Contratos de cliente (card 3643): Flat Fee e Gestão Ativa, com modelo versionado por tipo e minuta
 * por empresa. Rota base `api/v1/client-contracts`, permissões `CLIENTCONTRACT_*`.
 *
 * Texto travado: a minuta muda pelos campos do catálogo e pelos blocos opcionais; o backend
 * renderiza o texto a cada gravação. Só quem está na lista de editores do texto altera o corpo-fonte
 * (minuta) ou o modelo. Respostas `T` ou `ValidationResult<T>` (`unwrapContractResult`).
 */
@Injectable({
  providedIn: 'root',
})
export class ClientContractsHttpService {
  private readonly BASE_URL = 'client-contracts';

  constructor(private readonly apiService: VaultApiService) {}

  // --- Modelo por tipo ---

  /** Modelo vigente do tipo. Na primeira chamada do tenant o backend cria a v1 do texto padrão. */
  getCurrentTemplate(type: ClientContractType): Observable<ClientContractTemplateResponse | null> {
    return this.apiService
      .get<ClientContractTemplateResponse>(`${this.BASE_URL}/template`, undefined, { type })
      .pipe(unwrapContractResult<ClientContractTemplateResponse>());
  }

  /** Histórico de versões do modelo do tipo, sem o corpo. */
  getTemplateVersions(
    type: ClientContractType
  ): Observable<ClientContractTemplateVersionResponse[]> {
    return this.apiService
      .get<ClientContractTemplateVersionResponse[]>(
        `${this.BASE_URL}/template/versions`,
        undefined,
        {
          type,
        }
      )
      .pipe(
        unwrapContractResult<ClientContractTemplateVersionResponse[]>(),
        map(list => list ?? [])
      );
  }

  /** Uma versão do modelo, com o corpo, para leitura no histórico. */
  getTemplateVersion(id: string): Observable<ClientContractTemplateResponse | null> {
    return this.apiService
      .get<ClientContractTemplateResponse>(`${this.BASE_URL}/template/versions/${id}`)
      .pipe(unwrapContractResult<ClientContractTemplateResponse>());
  }

  /** Salva uma versão nova do modelo do `payload.type` (MANAGE_TEMPLATE e lista de editores). */
  saveTemplateVersion(
    payload: SaveClientContractTemplateRequest
  ): Observable<ClientContractTemplateResponse | null> {
    return this.apiService
      .post<ClientContractTemplateResponse>(`${this.BASE_URL}/template`, payload)
      .pipe(unwrapContractResult<ClientContractTemplateResponse>());
  }

  /** Variáveis aceitas no texto do tipo (`{{chave}}` e rótulo), para o botão Variável. */
  getVariables(type: ClientContractType): Observable<ClientContractVariableResponse[]> {
    return this.apiService
      .get<ClientContractVariableResponse[]>(`${this.BASE_URL}/variables`, undefined, { type })
      .pipe(
        unwrapContractResult<ClientContractVariableResponse[]>(),
        map(list => list ?? [])
      );
  }

  /** Catálogo do tipo: campos de valor (padrão, limites, casas) e blocos opcionais. */
  getFieldCatalog(type: ClientContractType): Observable<ClientContractFieldCatalogResponse> {
    return this.apiService
      .get<ClientContractFieldCatalogResponse>(`${this.BASE_URL}/fields`, undefined, { type })
      .pipe(
        unwrapContractResult<ClientContractFieldCatalogResponse>(),
        map(catalog => ({ fields: catalog?.fields ?? [], blocks: catalog?.blocks ?? [] }))
      );
  }

  /**
   * Outras empresas do(s) grupo(s) econômico(s) da empresa, para pré-preencher o bloco "Grupo
   * econômico" do GA. Lista vazia quando a empresa não está em grupo.
   */
  getEconomicGroupCompanies(companyId: string): Observable<ClientContractGroupCompany[]> {
    return this.apiService
      .get<ClientContractGroupCompany[]>(`${this.BASE_URL}/economic-group-companies`, undefined, {
        companyId,
      })
      .pipe(
        unwrapContractResult<ClientContractGroupCompany[]>(),
        map(list => list ?? [])
      );
  }

  /**
   * Prévia dos dados do cadastro da empresa e do que falta, para a modal Gerar contrato (antes de a
   * minuta existir). Permissão VIEW ou CREATE.
   */
  getAutomaticPreview(
    companyId: string
  ): Observable<ClientContractAutomaticPreviewResponse | null> {
    return this.apiService
      .get<ClientContractAutomaticPreviewResponse>(`${this.BASE_URL}/automatic`, undefined, {
        companyId,
      })
      .pipe(unwrapContractResult<ClientContractAutomaticPreviewResponse>());
  }

  // --- Minutas por empresa ---

  /** Contratos de uma empresa (permissão LIST). */
  getContractsByCompany(companyId: string): Observable<ClientContractListItemResponse[]> {
    return this.apiService
      .get<ClientContractListItemResponse[]>(this.BASE_URL, undefined, { companyId })
      .pipe(
        unwrapContractResult<ClientContractListItemResponse[]>(),
        map(list => list ?? [])
      );
  }

  /** Uma minuta, para o editor (permissão VIEW). */
  getContract(id: string): Observable<ClientContractResponse | null> {
    return this.apiService
      .get<ClientContractResponse>(`${this.BASE_URL}/${id}`)
      .pipe(unwrapContractResult<ClientContractResponse>());
  }

  /** Gera a minuta a partir do modelo vigente do tipo (campo ausente = padrão do catálogo). */
  createContract(payload: CreateClientContractRequest): Observable<ClientContractResponse | null> {
    return this.apiService
      .post<ClientContractResponse>(this.BASE_URL, payload)
      .pipe(unwrapContractResult<ClientContractResponse>());
  }

  /** Salva título, campos, blocos e (só editor do texto) o corpo-fonte. Só rascunho. */
  updateContract(
    id: string,
    payload: UpdateClientContractRequest
  ): Observable<ClientContractResponse | null> {
    return this.apiService
      .put<ClientContractResponse>(`${this.BASE_URL}/${id}`, payload)
      .pipe(unwrapContractResult<ClientContractResponse>());
  }

  /** Finaliza: o texto trava e os dados do cadastro ficam congelados até alguém reabrir. */
  finalizeContract(id: string): Observable<ClientContractResponse | null> {
    return this.apiService
      .post<ClientContractResponse>(`${this.BASE_URL}/${id}/finalize`, {})
      .pipe(unwrapContractResult<ClientContractResponse>());
  }

  /** Volta a minuta finalizada para rascunho (os dados do cadastro voltam a ser lidos ao vivo). */
  reopenContract(id: string): Observable<ClientContractResponse | null> {
    return this.apiService
      .post<ClientContractResponse>(`${this.BASE_URL}/${id}/reopen`, {})
      .pipe(unwrapContractResult<ClientContractResponse>());
  }

  /** Exclui a minuta (exclusão lógica). Só rascunho. */
  deleteContract(id: string): Observable<unknown> {
    return this.apiService
      .delete<unknown>(`${this.BASE_URL}/${id}`)
      .pipe(unwrapContractResult<unknown>());
  }

  /** Histórico de revisões, sem o corpo; a tela ordena da mais recente para a mais antiga. */
  getContractRevisions(id: string): Observable<ClientContractRevisionListItemResponse[]> {
    return this.apiService
      .get<ClientContractRevisionListItemResponse[]>(`${this.BASE_URL}/${id}/revisions`)
      .pipe(
        unwrapContractResult<ClientContractRevisionListItemResponse[]>(),
        map(list => list ?? [])
      );
  }

  /** Uma revisão com o texto renderizado daquela gravação. */
  getContractRevision(
    id: string,
    revisionId: string
  ): Observable<ClientContractRevisionResponse | null> {
    return this.apiService
      .get<ClientContractRevisionResponse>(`${this.BASE_URL}/${id}/revisions/${revisionId}`)
      .pipe(unwrapContractResult<ClientContractRevisionResponse>());
  }

  /** Alterações de valores (percentual ou R$): quem mudou, quando, de/para e o padrão. */
  getContractFieldChanges(id: string): Observable<ContractFieldChangeResponse[]> {
    return this.apiService
      .get<ContractFieldChangeResponse[]>(`${this.BASE_URL}/${id}/field-changes`)
      .pipe(
        unwrapContractResult<ContractFieldChangeResponse[]>(),
        map(list => list ?? [])
      );
  }

  /**
   * Baixa o PDF da minuta (permissão EXPORT_PDF). Mesmo caminho do contrato de parceria:
   * `downloadWithProgress` lê a mensagem de erro que chega como `Blob`, e o nome vem do
   * `Content-Disposition` (`filename*`, com acento).
   */
  downloadContractPdf(id: string, fallbackFileName: string): Observable<ContractPdfFile> {
    return this.apiService.downloadWithProgress('get', `${this.BASE_URL}/${id}/pdf`).pipe(
      filter(event => event.type === HttpEventType.Response),
      map(event => {
        const response = event as HttpResponse<Blob>;
        const fileName =
          fileNameFromContentDisposition(response.headers.get('Content-Disposition')) ??
          fallbackFileName;
        return { blob: response.body ?? new Blob([], { type: 'application/pdf' }), fileName };
      })
    );
  }
}
